import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";

interface Event {
  id?: number;
  event_name: string;
  location: string;
  time_of_year: string;
  focus_area: string;
  created_at?: string;
  country_id?: number;
  hidden?: boolean;
}

const EditEvent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [newEvent, setNewEvent] = useState<Event>({
    event_name: "",
    location: "",
    time_of_year: "",
    focus_area: "",
  });
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (id) {
      fetch(`/api/countries/${id}/events`)
        .then((res) => res.json())
        .then((data) => setEvents(data.filter((ev: Event) => !ev.hidden)))
        .catch(() => setError("Error fetching events"));
    }
  }, [id]);

  useEffect(() => {
    if (showPopup) {
      const handleDocumentClick = () => setShowPopup(false);
      document.addEventListener("click", handleDocumentClick);
      return () => document.removeEventListener("click", handleDocumentClick);
    }
  }, [showPopup]);

  const handleEventChange = (
    eventId: number,
    field: keyof Event,
    value: string
  ) => {
    setEvents((prev) =>
      prev.map((ev) => (ev.id === eventId ? { ...ev, [field]: value } : ev))
    );
  };

  const handleNewEventChange = (field: keyof Event, value: string) => {
    setNewEvent({ ...newEvent, [field]: value });
  };

  const handleUpdate = async (ev: Event) => {
    if (!ev.id) return;
    try {
      const response = await fetch(`/api/events/${ev.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventName: ev.event_name,
          location: ev.location,
          timeOfYear: ev.time_of_year,
          focusArea: ev.focus_area,
          countryId: Number(id),
          hidden: ev.hidden || false,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error updating event");
      }
    } catch {
      setError("Error connecting to the server");
    }
  };

  const handleNewSave = async () => {
    if (newEvent.event_name.trim() === "") return;
    try {
      const response = await fetch("/api/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventName: newEvent.event_name,
          location: newEvent.location,
          timeOfYear: newEvent.time_of_year,
          focusArea: newEvent.focus_area,
          countryId: Number(id),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error adding event");
      } else {
        const createdEvent = await response.json();
        setEvents((prev) => [createdEvent, ...prev]);
        setNewEvent({ event_name: "", location: "", time_of_year: "", focus_area: "" });
        setError("");
      }
    } catch {
      setError("Error connecting to the server");
    }
  };

  // HARD DELETE: remove event permanently
  const handleDeleteEvent = async (ev: Event) => {
    if (!ev.id) return;
    try {
      const response = await fetch(`/api/events/${ev.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error deleting event");
      } else {
        setEvents((prev) => prev.filter((e) => e.id !== ev.id));
        setShowDeleteModal(false);
        setEventToDelete(null);
        setConfirmText("");
      }
    } catch {
      setError("Error connecting to the server");
    }
  };

  const openDeleteModal = (ev: Event) => {
    setEventToDelete(ev);
    setConfirmText("");
    setShowDeleteModal(true);
  };

  const handleUpdateAll = async () => {
    if (newEvent.event_name.trim() !== "") {
      await handleNewSave();
    }
    for (const ev of events) {
      if (ev.event_name.trim() !== "") {
        await handleUpdate(ev);
      }
    }
    setShowPopup(true);
  };

  // style objects maintained as before...
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties = { backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", maxWidth: "600px", margin: "50px auto", padding: "30px" };
  const headerStyle: React.CSSProperties = { textAlign: "center", color: "#e41e26", borderBottom: "2px solid #e41e26", paddingBottom: "10px", marginBottom: "20px", fontSize: "24px" };
  const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
  const thStyle: React.CSSProperties = { textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" };
  const thRightAlignStyle: React.CSSProperties = { ...thStyle, textAlign: "right" };
  const tdStyle: React.CSSProperties = { padding: "8px", borderBottom: "1px solid #ccc" };
  const tdTrashStyle: React.CSSProperties = { ...tdStyle, textAlign: "right" };
  const inputStyle: React.CSSProperties = { width: "100%", padding: "5px", boxSizing: "border-box" };
  const buttonStyle: React.CSSProperties = { padding: "10px 20px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "16px", marginRight: "10px" };
  const navContainerStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", marginTop: "20px" };
  const popupStyle: React.CSSProperties = { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: "#e41e26", color: "#fff", padding: "15px 30px", borderRadius: "4px", boxShadow: "0 2px 5px rgba(0,0,0,0.3)", zIndex: 1000 };
  const trashButtonStyle: React.CSSProperties = { background: "none", border: "none", cursor: "pointer", fontSize: "18px", marginLeft: "10px" };
  const modalOverlayStyle: React.CSSProperties = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 };
  const modalContentStyle: React.CSSProperties = { backgroundColor: "#fff", padding: "20px", borderRadius: "8px", maxWidth: "400px", width: "90%", textAlign: "center" };

  return (
    <div style={containerStyle}>
      <Navbar />
      {showPopup && (
        <div style={popupStyle} onClick={() => setShowPopup(false)}>
          Updated
        </div>
      )}
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Events</h1>
        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Event Name</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Time of Year</th>
              <th style={thStyle}>Focus Area</th>
              <th style={thRightAlignStyle}></th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id}>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={ev.event_name}
                    onChange={(e) => handleEventChange(ev.id!, "event_name", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={ev.location}
                    onChange={(e) => handleEventChange(ev.id!, "location", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={ev.time_of_year}
                    onChange={(e) => handleEventChange(ev.id!, "time_of_year", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={ev.focus_area}
                    onChange={(e) => handleEventChange(ev.id!, "focus_area", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdTrashStyle}>
                  <button style={trashButtonStyle} onClick={() => openDeleteModal(ev)}>
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newEvent.event_name}
                  onChange={(e) => handleNewEventChange("event_name", e.target.value)}
                  style={inputStyle}
                  placeholder="New Event Name"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => handleNewEventChange("location", e.target.value)}
                  style={inputStyle}
                  placeholder="Location"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newEvent.time_of_year}
                  onChange={(e) => handleNewEventChange("time_of_year", e.target.value)}
                  style={inputStyle}
                  placeholder="Time of Year"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newEvent.focus_area}
                  onChange={(e) => handleNewEventChange("focus_area", e.target.value)}
                  style={inputStyle}
                  placeholder="Focus Area"
                />
              </td>
              <td style={tdStyle}></td>
            </tr>
          </tbody>
        </table>
        <div style={navContainerStyle}>
          <button style={buttonStyle} onClick={() => navigate(-1)}>
            Back
          </button>
          <button style={buttonStyle} onClick={handleUpdateAll}>
            Update
          </button>
        </div>
      </div>
      {showDeleteModal && eventToDelete && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to delete <strong>{eventToDelete.event_name}</strong>?<br />
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{ width: "80%", padding: "8px", margin: "10px 0" }}
            />
            <div style={{ marginTop: "10px" }}>
              <button
                style={buttonStyle}
                onClick={() => {
                  if (confirmText === "DELETE") {
                    handleDeleteEvent(eventToDelete);
                  } else {
                    alert("Please type DELETE to confirm.");
                  }
                }}
              >
                Submit
              </button>
              <button style={{ ...buttonStyle, marginLeft: "10px" }} onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditEvent;
